import { sort } from "@prismicio/editor-support/Array"
import { getRequestCredentials, request } from "@prismicio/editor-support/Http"
import { toURL } from "@prismicio/editor-support/URL"
import { z } from "zod"

import type { AuthStrategy } from "../../EditorConfig"
import { languageOkType } from "./document"

const repositoryAuthor = z.object({
  id: z.string(),
  firstname: z.string().optional(),
  lastname: z.string().optional(),
  email: z.string(),
  uploadedAvatar: z.string().optional()
})

const repositoryAuthors = z.array(repositoryAuthor)

export type RepositoryAuthor = z.TypeOf<typeof repositoryAuthor>

const repositoryQuotas = z.object({
  slicemachineEnabled: z.boolean()
})

export type RepositoryQuotas = z.TypeOf<typeof repositoryQuotas>

const supportedSliceMachineFrameworks = ["next", "nuxt", "sveltekit"] as const

type SupportedFramework = typeof supportedSliceMachineFrameworks[number]

function isSupportedFramework(value: string): value is SupportedFramework {
  return supportedSliceMachineFrameworks.includes(value as SupportedFramework)
}

const repositoryFramework = z.preprocess((value) => {
  // NOTE: we persist a lot of different frameworks in the DB, but only the SM supported are relevant to us
  // Any other framework is treated like "other"
  if (typeof value === "string" && isSupportedFramework(value)) {
    return value
  }
  return "other"
}, z.enum([...supportedSliceMachineFrameworks, "other"]))

export type RepositoryFramework = z.TypeOf<typeof repositoryFramework>

const repositorySchema = z
  .object({
    simulator_url: z.string().url().nullish(),
    languages: z
      .array(languageOkType)
      .transform((arr) => sort(arr, (lang) => lang.name)),
    authors: repositoryAuthors.optional().default([]),
    quotas: repositoryQuotas.optional().default({ slicemachineEnabled: false }),
    framework: repositoryFramework.optional().default("other"),
    storageVersion: z.object({
      major: z.number().int(),
      minor: z.number().int()
    })
  })
  .transform(({ simulator_url, framework, ...rest }) => {
    const simulatorUrl =
      simulator_url && isValidSimulatorURL(simulator_url)
        ? simulator_url
        : undefined

    return {
      ...rest,
      simulatorUrl,
      isSupportedFramework: framework !== "other"
    }
  })

export type Repository = z.TypeOf<typeof repositorySchema>

export function getRepository(
  baseUrl: URL,
  authStrategy: AuthStrategy
): Promise<Repository> {
  return request(new URL("repository", baseUrl), {
    credentials: getRequestCredentials(authStrategy),
    okType: repositorySchema
  })
}

export function isValidSimulatorURL(urlString: string) {
  const url = toURL(urlString)
  if (!url) return false
  return url.pathname.endsWith("/slice-simulator")
}

const repositoryTagsResponse = z.object({
  results: z.array(z.string())
})

type RepositoryTags = z.TypeOf<typeof repositoryTagsResponse>

export function getRepositoryTags(
  baseUrl: URL,
  authStrategy: AuthStrategy
): Promise<RepositoryTags> {
  return request(new URL("repository/tags", baseUrl), {
    credentials: getRequestCredentials(authStrategy),
    okType: repositoryTagsResponse
  })
}
